import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Check } from 'lucide-react';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';
import { listDatasets, createDataset, addItems } from 'features/datasets/api/datasetsApi';

/**
 * "Add to dataset…" for a SELECTION of units (the Spans / Runs tables' bulk
 * bar, the eval results page): pick a dataset or name a new one, and the
 * selected span_ids / run_ids / conversation_ids go in through the shared
 * unit path — Input, Output and Evaluations filled, units already present
 * skipped. Reports "N added · M already there" for a moment afterwards.
 */
export default function AddToDatasetButton({ selection, label = 'Add to dataset…', onDone, disabled = false }) {
  const [open, setOpen] = useState(false);
  // On an eval's results, the ticked rows can go in as the units scored or as the verdicts themselves.
  const canVerdicts = Array.isArray(selection?.eval_run_ids) && selection.eval_run_ids.length > 0;
  const [rows, setRows] = useState('units');
  const [datasets, setDatasets] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const box = useRef(null);

  const asVerdicts = canVerdicts && rows === 'verdicts';
  const payload = asVerdicts
    ? { eval_run_ids: selection.eval_run_ids }
    : Object.fromEntries(Object.entries(selection || {}).filter(([k]) => k !== 'eval_run_ids'));
  const count = asVerdicts ? selection.eval_run_ids.length : ['span_ids', 'run_ids', 'conversation_ids'].reduce((n, k) => n + ((selection?.[k] || []).length), 0);

  useEffect(() => {
    if (!open) return undefined;
    if (datasets === null) listDatasets().then((d) => setDatasets(d || [])).catch(() => setDatasets([]));
    const onDoc = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, datasets]);

  const finish = (res, dataset) => {
    setOpen(false);
    setResult({ added: res.added || 0, skipped: res.skipped || 0, dataset });
    // The caller usually clears its selection, which removes the bar this
    // button sits in; wait until the result has been read.
    setTimeout(() => { setResult(null); onDone?.(res, dataset); }, 2500);
  };
  const failed = (e) => setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not add the rows.');

  const addTo = async (dataset) => {
    setBusy(true); setError(null);
    try { finish(await addItems(dataset.id, payload), dataset); } catch (e) { failed(e); } finally { setBusy(false); }
  };
  const createAndAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true); setError(null);
    try {
      const ds = await createDataset(trimmed);
      setDatasets(null);
      finish(await addItems(ds.id, payload), ds);
    } catch (e) { failed(e); } finally { setBusy(false); }
  };

  return (
    <div ref={box} className="relative">
      <button type="button" disabled={disabled || !count || busy} onClick={() => { setOpen((v) => !v); setError(null); }} aria-haspopup="menu" aria-expanded={open}
        className={`${activityNeutralButtonClass} disabled:opacity-50`}>
        {result ? <><Check size={13} /> {result.added} added{result.skipped ? ` · ${result.skipped} already there` : ''}</> : label}
      </button>
      {open ? (
        <div role="menu" aria-label="Add to dataset" className="absolute bottom-[calc(100%+6px)] left-0 z-40 w-72 overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-token-lg">
          <div className="border-b border-[color:var(--border-subtle)] px-3 py-2 text-[11px] text-[color:var(--text-muted)]">{count} selected</div>
          {canVerdicts ? (
            <div className="flex gap-3 border-b border-[color:var(--border-subtle)] px-3 py-1.5 text-[11px]" role="radiogroup" aria-label="Rows are">
              {[['units', 'What it scored'], ['verdicts', 'The verdicts']].map(([v, l]) => (
                <label key={v} className="flex items-center gap-1 text-[color:var(--text-primary)]">
                  <input type="radio" name="atd-rows" checked={rows === v} onChange={() => setRows(v)} /> {l}
                </label>
              ))}
            </div>
          ) : null}
          <div className="max-h-56 overflow-y-auto py-1">
            {datasets === null ? <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">Loading…</div> : null}
            {datasets && !datasets.length ? <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">No datasets yet. Name one below.</div> : null}
            {(datasets || []).map((d) => (
              <button key={d.id} type="button" role="menuitem" disabled={busy} onClick={() => addTo(d)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-[color:var(--text-primary)] hover:bg-surface-hover disabled:opacity-50">
                <span className="min-w-0 flex-1 truncate">{d.name || 'Untitled'}</span>
                <span className="flex-none text-[11px] tabular-nums text-[color:var(--text-muted)]">{(Number(d.item_count) || 0).toLocaleString()}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 border-t border-[color:var(--border-default)] px-2.5 py-2">
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') createAndAdd(); e.stopPropagation(); }}
              placeholder="New dataset name" aria-label="New dataset name"
              className="min-w-0 flex-1 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 py-1 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]" />
            <button type="button" disabled={busy || !name.trim()} onClick={createAndAdd}
              className="inline-flex flex-none items-center gap-1 rounded-md bg-brand px-2 py-1 text-[12px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} New
            </button>
          </div>
          {error ? <div className="border-t border-[color:var(--border-default)] px-3 py-1.5 text-[11px] text-danger">{error}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
